import React from "react";
import {
  Chart,
  ChartLegend,
  ChartSeries,
  ChartSeriesItem,
  ChartXAxis,
  ChartXAxisItem,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartTooltip,
  ChartArea,
  ChartValueAxis,
  ChartValueAxisItem,
} from "@progress/kendo-react-charts";

interface DataPoint {
  capacity: number;
  [key: string]: number;
}

interface HeadDataPoint extends DataPoint {
  head: number;
}

interface BhpDataPoint extends DataPoint {
  bhp: number;
}

interface EfficiencyDataPoint extends DataPoint {
  efficiency: number;
}

interface Units {
  capacity: string;
  head: string;
  bhp: string;
  efficiency: string;
}

interface MaxScales {
  capacity: number;
  head: number;
  bhp: number;
  efficiency: number;
}

interface TargetDataPoint extends DataPoint {
  head: number;
}

interface MinMaxFlow {
  gkxMin: number;
  gkxMax: number;
  mainPmpMax: number;
  mainPmpMin: number;
  chargePmp1Max: number;
  chargePmp1Min: number;
  chargePmp2Max: number;
  chargePmp2Min: number;
  chargePmp3Max: number;
  chargePmp3Min: number;
  maxBPDRange: number;
}

interface PumpNames {
  gkxPump: string;
  mainPump: string;
  chargePump1?: string;
  changePump2?: string;
  chargePump2?: string;
  changePump3?: string;
}

export interface CompositePumpCapacityCurveGraphData {
  head: HeadDataPoint[];
  bhp: BhpDataPoint[];
  efficiency: EfficiencyDataPoint[];
  units: Units;
  maxScales: MaxScales;
  target: TargetDataPoint[];
  targetV: TargetDataPoint[];
  targetH: TargetDataPoint[];
  minMaxFlow: MinMaxFlow;
  pumpNames: PumpNames;
}

interface FlowRangeChartProps {
  data: CompositePumpCapacityCurveGraphData;
}

const FlowRangeChart: React.FC<FlowRangeChartProps> = ({ data }) => {
  const scale = Number(data?.minMaxFlow?.maxBPDRange) / 1000;
  const xAxisMax = Math.ceil(Math.ceil(scale) * (1000 * (1 / scale) * scale));
  const xAxisCrossingValues = [0, xAxisMax, xAxisMax];

  // Common styles
  const gridLineColor = "#005F7C";
  const crosshairColor = "#005F7C";

  // Series configuration with conditional logic
  const seriesConfig = [];
  if (data?.minMaxFlow?.mainPmpMin >= 0 && data?.minMaxFlow?.mainPmpMax > 0) {
    seriesConfig.push({
      name: "Main Pump",
      data: [
        {
          category: data.pumpNames.mainPump,
          from: data.minMaxFlow.mainPmpMin,
          to: data.minMaxFlow.mainPmpMax,
        },
      ],
      color: "#DC4405",
    });
  }
  if (
    data?.minMaxFlow?.chargePmp3Min >= 0 &&
    data?.minMaxFlow?.chargePmp3Max > 0
  ) {
    seriesConfig.push({
      name: data.pumpNames.changePump3 || "Charge Pump 3",
      data: [
        {
          category: data.pumpNames.changePump3 || "Charge Pump 3",
          from: data.minMaxFlow.chargePmp3Min,
          to: data.minMaxFlow.chargePmp3Max,
        },
      ],
      color: "#003E51",
    });
  }
  if (
    data?.minMaxFlow?.chargePmp2Min >= 0 &&
    data?.minMaxFlow?.chargePmp2Max > 0
  ) {
    seriesConfig.push({
      name: "Charge Pump 2",
      data: [
        {
          category: data.pumpNames.changePump2 || "Charge Pump 2",
          from: data.minMaxFlow.chargePmp2Min,
          to: data.minMaxFlow.chargePmp2Max,
        },
      ],
      color: "#6BBBAE",
    });
  }
  if (
    data?.minMaxFlow?.chargePmp1Min >= 0 &&
    data?.minMaxFlow?.chargePmp1Max > 0
  ) {
    seriesConfig.push({
      name: "Charge Pump 1",
      data: [
        {
          category: data.pumpNames.chargePump1 || "Charge Pump 1",
          from: data.minMaxFlow.chargePmp1Min,
          to: data.minMaxFlow.chargePmp1Max,
        },
      ],
      color: "#FFBF3F",
    });
  }
  if (data?.minMaxFlow?.gkxMin >= 0 && data?.minMaxFlow?.gkxMax > 0) {
    seriesConfig.push({
      name: "GKX Pump",
      data: [
        {
          category: data.pumpNames.gkxPump,
          from: data.minMaxFlow.gkxMin,
          to: data.minMaxFlow.gkxMax,
        },
      ],
      color: "#00857D",
    });
  }
  return (
    <Chart className='flex justify-center w-[92%]' style={{ height: "200px" }}>
      <ChartArea background='transparent' height={180} />
      <ChartLegend visible={false} position='bottom' />
      <ChartValueAxis>
        <ChartValueAxisItem
          labels={{
            color: "white",
          }}
          color="#fff"
        />
      </ChartValueAxis>

      {/* Series */}
      <ChartSeries>
        {seriesConfig.map((series) => (
          <ChartSeriesItem
            key={series.name}
            name={series.name}
            type="rangeBar"
            data={series.data}
            categoryField="category"
            fromField="from"
            toField="to"
            color={series.color}
            style="smooth"
            width={10}
            notes={{ label: { position: "inside" } }}
            tooltip={{ visible: true, format: "Min: {0}, Max: {1}", color: "#FFF" }}
            spacing={-1}
            gap={0}
          />
        ))}
      </ChartSeries>

      {/* X Axis */}
      <ChartXAxis>
        <ChartXAxisItem
          name="Capacity"
          min={0}
          max={xAxisMax}
          axisCrossingValue={xAxisCrossingValues}
          majorGridLines={{ width: 0.8, color: gridLineColor }}
          minorGridLines={{ width: 0.3, color: gridLineColor, visible: true }}
          crosshair={{
            visible: true,
            width: 1,
            color: crosshairColor,
            tooltip: { visible: true, format: "#.##" },
          }}
        />
      </ChartXAxis>

      {/* Category Axis */}
      <ChartCategoryAxis>
        <ChartCategoryAxisItem
          rangeLabels={{ visible: true, position: "onAxis" }}
          majorGridLines={{ visible: false }}
          minorTicks={{ visible: false }}
          labels={{ position: "onAxis", color: "#fff", visible: true }}
          color='#fff'
        />
      </ChartCategoryAxis>

      {/* Tooltip */}
      <ChartTooltip
        visible={true}
        color="red"
        opacity={1}
        format="Flow= {0}, Head= {1}"
      />
    </Chart>
  );
};

export default FlowRangeChart;
